/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { ensureAuthenticated } from '../../utilities/is-authenticated';
import { trackedAction } from '../../utilities/track-execution-time';
import { escapeODataStringLiteral } from '../../utilities/odata';

/**
 * GetDocumentDownload Input Action
 */
export class GetDocumentDownloadInput implements Msdyn365.IActionInput {
    public documentId: string;
    constructor(documentId: string) {
        this.documentId = documentId;
    }

    // a cache object type and an appropriate cache key
    public getCacheKey = () => `getDocumentDownload-${this.documentId}`;
    public getCacheObjectType = () => 'GetDocumentDownload';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

export interface IGetDocumentDownloadData {
    '@odata.context'?: string;
    FileName: string;
    Length: string;
    Type: string;
    Content: string;
    RecordCreatedDate: Date;
    ExtensionProperties: unknown[];
}

const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new GetDocumentDownloadInput('');
};

async function action(input: GetDocumentDownloadInput, ctx: Msdyn365.IActionContext): Promise<IGetDocumentDownloadData> {
    ensureAuthenticated(ctx);
    let getDocumentDownloadData: IGetDocumentDownloadData = {} as IGetDocumentDownloadData;

    await Msdyn365.sendRequest(
        `${ctx.requestContext.apiSettings.baseUrl}Commerce/Document/GetDocument(documentId='${escapeODataStringLiteral(
            input.documentId
        )}')`,
        'get',
        {},
        {
            headers: {
                OUN: ctx.requestContext.apiSettings.oun,
                Authorization: `id_token ${ctx.requestContext.user.token}`
            }
        }
    )
        .then(res => {
            getDocumentDownloadData = res?.data as IGetDocumentDownloadData;
        })
        .catch(error => {
            console.error('Error fetching getDocumentDownloadData', error);
        });
    return getDocumentDownloadData;
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetDocumentDownloadData>>((input, ctx) => trackedAction(action, input, ctx, 'GetDocument')),
    id: 'GetDocumentDownload',
    input: createInput
});
